import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";

import { LABELS } from "~/resources/Labels";
import { MESSAGES } from "~/resources/Message";

import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import { SIMPLDialog } from "../../components/commons/dialog/SimplDialog";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SliderField } from "../../components/commons/slider/SIMPLSlider";

import { UseVmTemplateForm } from "./UseVmTemplateForm";

import {
  extractCloudProviderList,
  extractComponentsDetails,
  extractEnvDetails,
  getProviders
} from "~/Utils/Utils";
import {
  CloudProvisionerResponse,
  FormCloudEnvironment,
} from "~/models/Commons ";
import {
  useGetCloudProvisionerFullQuery,
  useGetCloudProvisionerQuery,
} from "~/services/cloudProvisioner";

// -------------------- Types --------------------
interface Prop {
  onCloseModal: () => void;
  refetch: () => void;
  editedItem: FormCloudEnvironment | null;
  setConfig: Function;
  title?: string;
}

export const AddVmTemplate = ({
  onCloseModal,
  editedItem,
  setConfig,
  title,
  refetch,
}: Prop) => {
  const {
    handleSubmit,
    control,
    errors,
    cloudEnvData,
    cloudProviderName,
    ConfigurationComponentsData,
    PolicyComponentsData,
    ProvissioningComponentsData,
    providerData,
    submitForm,
    setValue,
    watch,
    resetField,
  } = UseVmTemplateForm(onCloseModal, refetch,undefined!);

  // -------------------- State --------------------
  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);
  const [cpu, setCpu] = useState<{ min: number; max: number } | null>(null);
  const [ram, setRam] = useState<{ min: number; max: number } | null>(null);
  const [storage, setStorage] = useState<{ min: number; max: number } | null>(
    null
  );
  const [cloudProvisionerTemplateId, setCloudProvisionerTemplateId] = useState<
    number | undefined
  >(undefined);
  const [osOptions, setOsOptions] = useState<string[]>([]);
  const [azureVmSizeOptions, setAzureVmSizeOptions] = useState<string[]>([]);
  const [awsInstanceTypeOptions, setAwsInstanceTypeOptions] = useState<string[]>([]);
  const [selectClouldProvisioner, setSelectClouldProvisioner] = useState<any[]>(
    []
  );
    const [clouldProvisionerList, setClouldProvisionerList] = useState<any[]>(
    []
  );
  const [selectedCloudEnvironments, setSelectedCloudEnvironments] = useState<any[]>([]);
  const [selectCloudEnv, setSelectCloudEnv] = useState<any[]>([]);
  const [selectProvider, setSelectProvider] = useState<any[]>([]);
  const [selectVmConfiguration, setSelectVmConfiguration] = useState<any[]>([]);
  const [selectPostProvisioning, setSelectPostProvisioning] = useState<any[]>(
    []
  );
  const [selectPolicy, setSelectPolicy] = useState<any[]>([]);
  const [showAzureInput, setShowAzureInput] = useState<boolean>(false);
  const [showAwsInput, setShowAwsInput] = useState<boolean>(false);
  const { data } = useGetCloudProvisionerFullQuery();
  const { data: provisionerData } = useGetCloudProvisionerQuery(
    cloudProvisionerTemplateId!,
    { skip: cloudProvisionerTemplateId === undefined }
  );

  // -------------------- Handlers --------------------
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(submitForm)(event);
    setConfig({
      key: "",
      direction: LABELS.ASC,
    });
  };
const fetchProviderList = () => {
    setCloudProviderList(getProviders(providerData));
  };
  useEffect(() => {
    fetchProviderList();
  }, [providerData]);
  const getCloudProviderOptions = (
    provider: { cloudProviderName: string; id: number }[]
  ) =>
    provider?.map((cloudEnv) => ({
      label: cloudEnv.cloudProviderName,
      value: cloudEnv.id,
    }));

  const handleCloudProviderChange = (
    value: number,
    onChange: (val: number) => void
  ) => {
    onChange(value);
    const selected = cloudProviderName.find((p) => p.id === value);
    const isAzure = selected?.cloudProviderName === LABELS.Azure;
    const isAws = selected?.cloudProviderName === "AWS";
    setShowAzureInput(isAzure);
    setShowAwsInput(isAws);

    resetField("os");
    resetField("cpuCore");
    resetField("ram");
    resetField("storage");
    resetField("azureVmSize");
    resetField("awsInstanceType");
  };
  const initVmTemplate = () => {
    const payloadData = data?.payload as any | undefined;
    const provisionerPayload = provisionerData?.payload as
      | CloudProvisionerResponse
      | undefined;
    const envPayload = cloudEnvData?.payload as any | undefined;
    const configPayload = ConfigurationComponentsData?.payload as
      | any
      | undefined;
    const postProvisionPayload = ProvissioningComponentsData?.payload as
      | any
      | undefined;
    const policyPayload = PolicyComponentsData?.payload as any | undefined;
    if (payloadData?.content) {
      setClouldProvisionerList(payloadData?.content)
      setSelectClouldProvisioner(
        payloadData.content.map(({ id, fileName }: any) => ({ id, fileName }))
      );
    }

    // Cloud environment
    if (envPayload) setSelectCloudEnv(extractEnvDetails(envPayload.content));
    setSelectedCloudEnvironments(envPayload?.content)
    // Populate Select Provider
    let optionProvider = extractCloudProviderList(envPayload?.content);
    setSelectProvider(optionProvider);

    // VM configuration
    if (configPayload)
      setSelectVmConfiguration(extractComponentsDetails(configPayload.content));

    // Post-provisioning
    if (postProvisionPayload)
      setSelectPostProvisioning(
        extractComponentsDetails(postProvisionPayload.content)
      );

    // Policy
    if (policyPayload)
      setSelectPolicy(extractComponentsDetails(policyPayload.content));

    // Provisioner limits
    if (provisionerPayload) {
      const {
        minCpuCore,
        maxCpuCore,
        minRam,
        maxRam,
        minStorage,
        maxStorage,
        os,
        azureVmSizes,
      } = provisionerPayload;
      setCpu({ min: minCpuCore, max: maxCpuCore });
      setRam({ min: minRam, max: maxRam });
      setStorage({ min: minStorage, max: maxStorage });
      setOsOptions(os);
      setAzureVmSizeOptions(azureVmSizes ?? []);
      setAwsInstanceTypeOptions(provisionerPayload.awsInstanceTypes ?? []);
    }

  };

  // -------------------- Effects --------------------
  useEffect(() => {
    initVmTemplate();
  }, [
    data,
    provisionerData,
    cloudEnvData,
    ConfigurationComponentsData,
    PolicyComponentsData,
    editedItem,
  ]);

 // Watch the selected provider
const provider = watch("providerType");

// Update UI and reset environment when provider changes
useEffect(() => {
  if (!provider) return;
  setShowAzureInput(provider.label === "Azure");
  setShowAwsInput(provider.label === "AWS");
  setValue("cloudEnvironmentId", null);
  setValue("cloudProvisionerTemplateId",null)
}, [provider, setValue]);

// Filter environments based on selected provider
const filteredEnvironments = provider
  ? selectedCloudEnvironments?.filter(
      (env) => env.cloudProvider?.cloudProviderName === provider.label
    ) ?? []
  : selectCloudEnv;

  // Filter cloudProvisionerTemplate based on selected provider
const filteredCloudProvisionerTemplate = provider
  ? clouldProvisionerList?.filter((env) =>
      provider.label === "Azure" ? env.isAzure : provider.label === "AWS" ? env.isAws : true
    ) ?? []
  : selectClouldProvisioner;


  const watchProvisioner = watch("cloudProvisionerTemplateId");
  useEffect(() => {
    setCloudProvisionerTemplateId(watchProvisioner?.value || undefined);
  }, [watchProvisioner, provisionerData,provider]);

  // -------------------- UI Helpers --------------------
  const renderButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label="Cancel"
          secondary
          buttonHandlerAction={onCloseModal}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized label={LABELS.Submit} primary />
      </div>
    </>
  );

  // -------------------- Render --------------------
  return (
    <SIMPLDialog
      onCloseModal={onCloseModal}
      title={editedItem ? LABELS.EditScript : title!}
      actions={renderButtons()}
      onSubmit={handleFormSubmit}
    >
      <div className="flex justify-center">
        <div className="w-[500px]">
          <div className="flex flex-col w-full gap-y-4">
            {/* Name */}
            <SIMPLInput
              name="name"
              control={control}
              rules={{
                required: { value: true, message: LABELS.NameRequired },
              }}
              label={LABELS.Name}
              placeholder={LABELS.Name}
              required
              id="name"
              fontWeight="bold"
              errorLabel={errors.name?.message}
            />

            {/* Description */}
            <SIMPLInput
              name="description"
              control={control}
              label={LABELS.Description}
              id="description"
              fontWeight="bold"
              errorLabel={errors.description?.message}
              placeholder={LABELS.Description}
            />
               {/* Provider */}
            <div className="h-auto">
              <Controller
                name="providerType"
                control={control}
                rules={{ required: MESSAGES.MessageCloudProviderRequired }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="providerType"
                    label={LABELS.CloudProviderWithSpace}
                    options={getCloudProviderOptions(cloudProviderList)}
                    isRequired
                    value={field.value}
                    onChange={(val: any) =>
                      handleCloudProviderChange(val, field.onChange)
                    }
                    hideErrorLabel
                    errorLabel={errors.providerType?.message as string}
                    placeholder={LABELS.CloudProviderWithSpace}
                  />
                )}
              />
            </div>
            {/* Cloud Environment */}
            <div className="h-auto">
              <Controller
                name="cloudEnvironmentId"
                control={control}
                rules={{ required: MESSAGES.MessageCloudEnvironmentRequired }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="cloudEnvironmentId"
                    label={LABELS.CloudEnviroment}
                    options={filteredEnvironments?.map((cloudEnv) => ({
                      label: cloudEnv.environmentName,
                      value: cloudEnv.id,
                    }))}
                    isRequired
                    isDisabled={!provider}
                    value={field.value}
                    onChange={field.onChange}
                    hideErrorLabel
                    errorLabel={errors.cloudEnvironmentId?.message as string}
                    placeholder={LABELS.CloudEnviroment}
                  />
                )}
              />
            </div>
           
            {/* Provisioning Template */}
            <div className="h-auto">
              <Controller
                name="cloudProvisionerTemplateId"
                control={control}
                rules={{
                  required: MESSAGES.MessageProvisioningTemplateRequired,
                }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="cloudProvisionerTemplateId"
                    label={LABELS.ProvisioningTemplate}
                    options={filteredCloudProvisionerTemplate.map((template) => ({
                      label: template.fileName,
                      value: template.id,
                    }))}
                    isRequired
                    isDisabled={!provider}
                    value={field.value}
                    onChange={field.onChange}
                    hideErrorLabel
                    errorLabel={
                      errors.cloudProvisionerTemplateId?.message as string
                    }
                    placeholder={LABELS.ProvisioningTemplate}
                  />
                )}
              />
            </div>

            {/* VM Configuration */}
            <div className="h-auto">
              <Controller
                name="vmconfiguration"
                control={control}
                rules={{
                  validate: (value) =>
                    value || watch("vmPostProviosiing") || watch("vmPolicy")
                      ? true
                      : MESSAGES.MessageVmConfigurationRequired,
                }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="vmconfiguration"
                    label={LABELS.VmConfiguration}
                    options={selectVmConfiguration.map((config) => ({
                      label: config.name,
                      value: config.id,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    hideErrorLabel
                    errorLabel={errors.vmconfiguration?.message as string}
                    placeholder={LABELS.VmConfiguration}
                  />
                )}
              />
            </div>

            {/* Post-Provisioning */}
            <div className="h-auto">
              <Controller
                name="vmPostProviosiing"
                control={control}
                rules={{
                  validate: (value) =>
                    value || watch("vmconfiguration") || watch("vmPolicy")
                      ? true
                      : MESSAGES.MessageVmPostProvisioningRequired,
                }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="vmPostProviosiing"
                    label={LABELS.VmProvisioning}
                    options={selectPostProvisioning.map((post) => ({
                      label: post.name,
                      value: post.id,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    hideErrorLabel
                    errorLabel={errors.vmPostProviosiing?.message as string}
                    placeholder={LABELS.VmProvisioning}
                  />
                )}
              />
            </div>

            {/* Policy */}
            <div className="h-auto">
              <Controller
                name="vmPolicy"
                control={control}
                rules={{
                  validate: (value) =>
                    value ||
                    watch("vmconfiguration") ||
                    watch("vmPostProviosiing")
                      ? true
                      : MESSAGES.MessageVmPolicyRequired,
                }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="vmPolicy"
                    label={LABELS.VmPolicy}
                    options={selectPolicy.map((policy) => ({
                      label: policy.name,
                      value: policy.id,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    hideErrorLabel
                    errorLabel={errors.vmPolicy?.message as string}
                    placeholder={LABELS.VmPolicy}
                  />
                )}
              />
            </div>

            {/* OS / Resources — generic (no cloud-specific sizing) */}
            {!showAzureInput && !showAwsInput && (
              <>
                {/* OS */}
                <div className="h-auto">
                  <Controller
                    name="os"
                    control={control}
                    rules={{
                      required: { value: true, message: MESSAGES.MessageOsRequired },
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="os"
                        label={LABELS.Os}
                        options={osOptions.map((os) => ({ label: os, value: os }))}
                        value={field.value}
                        onChange={field.onChange}
                        errorLabel={errors.os?.message as string}
                        placeholder={LABELS.SelectOs}
                        hideErrorLabel
                        isRequired
                      />
                    )}
                  />
                </div>

                {/* CPU */}
                <div className="h-auto">
                  <Controller
                    name="cpuCore"
                    control={control}
                    rules={{
                      required: { value: true, message: MESSAGES.MessageCpuCoreRequired },
                    }}
                    render={({ field }) => (
                      <SliderField
                        id="cpuCores"
                        label={LABELS.CpuCore}
                        value={field.value}
                        min={cpu?.min}
                        max={cpu?.max}
                        valueLabel={LABELS.CpuCoreValue}
                        onChange={field.onChange}
                        errorLabel={errors.cpuCore?.message as string}
                        required
                      />
                    )}
                  />
                </div>

                {/* RAM */}
                <div className="h-auto">
                  <Controller
                    name="ram"
                    control={control}
                    rules={{
                      required: { value: true, message: MESSAGES.MessageRamRequired },
                    }}
                    render={({ field }) => (
                      <SliderField
                        id="ram"
                        label={LABELS.Ram}
                        min={ram?.min}
                        max={ram?.max}
                        value={field.value}
                        valueLabel={LABELS.RamValue}
                        onChange={field.onChange}
                        errorLabel={errors.ram?.message as string}
                        required
                      />
                    )}
                  />
                </div>

              </>
            )}

            {/* Azure-specific inputs: OS + VM Size */}
            {showAzureInput && (
              <>
                {/* OS */}
                <div className="h-auto">
                  <Controller
                    name="os"
                    control={control}
                    rules={{
                      required: { value: true, message: MESSAGES.MessageOsRequired },
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="os"
                        label={LABELS.Os}
                        options={osOptions.map((os) => ({ label: os, value: os }))}
                        value={field.value}
                        onChange={field.onChange}
                        errorLabel={errors.os?.message as string}
                        placeholder={LABELS.SelectOs}
                        hideErrorLabel
                        isRequired
                      />
                    )}
                  />
                </div>

                {/* Azure VM Size */}
                <div className="h-auto">
                  <Controller
                    name="azureVmSize"
                    control={control}
                    rules={{
                      required: { value: true, message: MESSAGES.MessageAzureVmSizeRequired },
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="azureVmSize"
                        label={LABELS.AzureVmSize}
                        options={azureVmSizeOptions.map((s) => ({ label: s, value: s }))}
                        isRequired
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel
                        errorLabel={errors.azureVmSize?.message as string}
                        placeholder={LABELS.AzureVmSize}
                      />
                    )}
                  />
                </div>
              </>
            )}

            {/* AWS-specific inputs: OS + Instance Type */}
            {showAwsInput && (
              <>
                {/* OS */}
                <div className="h-auto">
                  <Controller
                    name="os"
                    control={control}
                    rules={{
                      required: { value: true, message: MESSAGES.MessageOsRequired },
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="os"
                        label={LABELS.Os}
                        options={osOptions.map((os) => ({ label: os, value: os }))}
                        value={field.value}
                        onChange={field.onChange}
                        errorLabel={errors.os?.message as string}
                        placeholder={LABELS.SelectOs}
                        hideErrorLabel
                        isRequired
                      />
                    )}
                  />
                </div>

                {/* AWS Instance Type */}
                <div className="h-auto">
                  <Controller
                    name="awsInstanceType"
                    control={control}
                    rules={{
                      required: { value: true, message: MESSAGES.MessageAwsInstanceTypesRequired },
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="awsInstanceType"
                        label={LABELS.AwsInstanceType}
                        options={awsInstanceTypeOptions.map((s) => ({ label: s, value: s }))}
                        isRequired
                        value={field.value}
                        onChange={field.onChange}
                        hideErrorLabel
                        errorLabel={errors.awsInstanceType?.message as string}
                        placeholder={LABELS.AwsInstanceType}
                      />
                    )}
                  />
                </div>
              </>
            )}

            {/* Disk Size (GB) — common to all cloud providers */}
            <div className="h-auto">
              <SIMPLInput
                name="storage"
                control={control}
                type="number"
                rules={{
                  required: { value: true, message: MESSAGES.MessageDiskSizeRequired },
                  min: {
                    value: Math.max(30, storage?.min ?? 30),
                    message: `Disk size must be ≥ ${Math.max(30, storage?.min ?? 30)} GB`,
                  },
                  ...(storage?.max != null && {
                    max: { value: storage.max, message: `Disk size must be ≤ ${storage.max} GB` },
                  }),
                }}
                label={LABELS.DiskSize}
                placeholder={LABELS.DiskSize}
                id="storage"
                fontWeight="bold"
                required
                errorLabel={errors.storage?.message as string}
              />
            </div>
          </div>
        </div>
      </div>
    </SIMPLDialog>
  );
};
